import React from 'react';
import { Box, useToast } from '@chakra-ui/react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Components
import Header from './components/Header';
import Footer from './components/Footer';

// Pages
import HomePage from './pages/HomePage';
import PackagesPage from './pages/PackagesPage';
import PackageDetailPage from './pages/PackageDetailPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import UserPackagesPage from './pages/UserPackagesPage';
import NotFoundPage from './pages/NotFoundPage';
import AboutPage from './pages/AboutPage';

// Auth Context
import { AuthProvider, useAuth } from './context/AuthContext';

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const toast = useToast();
  
  if (!isAuthenticated) {
    // Show toast notification
    toast({
      title: 'Authentication required',
      description: 'You need to be logged in to access this page.',
      status: 'warning',
      duration: 5000,
      isClosable: true,
    });
    
    // Redirect to login with the return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/packages" element={<PackagesPage />} />
      <Route path="/packages/:packageId" element={<PackageDetailPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      
      {/* Protected Routes */}
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/packages" element={
        <ProtectedRoute>
          <UserPackagesPage />
        </ProtectedRoute>
      } />
      
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Box minH="100vh" display="flex" flexDir="column">
        <Header />
        <Box flex="1" as="main" px={4} py={8} maxW="1200px" mx="auto" w="100%">
          <AppRoutes />
        </Box>
        <Footer />
      </Box>
    </AuthProvider>
  );
};

export default App;
